import { useInfiniteQuery } from '@tanstack/react-query';
import type { ResponseDataWithPagination } from '@/types/apiTypes';
import { useFetch } from '@/hooks/useFetch';
import { getInfinityUrl } from '../../utils/getInfinityUrl';

interface UseFetchInfinityOptionsProps {
  url: string;
  queryKey: string[];
  searchValue: string;
  staleTime?: number;
  gcTime?: number;
}

export const useFetchInfinityOptions = <T extends ResponseDataWithPagination<unknown>>({
  url,
  queryKey,
  searchValue,
  staleTime,
  gcTime,
}: UseFetchInfinityOptionsProps) => {
  const { doFetch } = useFetch<void, T>();
  const fetchRelationshipTypes = async ({ pageParam = 1 }) => {
    const results = await doFetch(getInfinityUrl({ url, search: searchValue, page: pageParam }));

    return {
      ...results,
      nextCursor: results.pagination?.last_page !== results.pagination?.current_page ? pageParam + 1 : undefined,
    };
  };

  const queryResult = useInfiniteQuery({
    queryKey: [...queryKey, searchValue],
    queryFn: fetchRelationshipTypes,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 1,
    staleTime,
    gcTime,
  });

  return {
    ...queryResult,
    data: queryResult.data?.pages.flatMap((page) => page.data) ?? [],
    pagination: queryResult.data?.pages.at(-1)?.pagination,
  };
};